import AppContext from 'App/AppContext';
import { IPStack } from 'baseComponents';
import { canUpdateTaskField, TaskFieldTypes } from 'components/Tasks/TaskAuthHelper';
import { globalStackItemStylesPaddingScene, globalStackTokensGapSmall } from 'globalStyles';
import Task from 'models/tasks/task';
import { TaskCheckList } from 'models/tasks/taskHelperClasses';
import { useContext } from 'react';
import CheckList from './Checklist';
import ChecklistProgress from './ChecklistProgress';

export enum ChecklistMode {
  Creator = 1, //can edit the checklist and pre-fill item comments (instructions), but not set item states. No progress bar.
  User = 2, //can fill the checklist (status+comments). Depending on permissions can also edit it. Shows progress bar.
}

interface IChecklistContainerProps {
  task: Task;
  orgTask: Task;
  canUpdate: boolean;
  mode: ChecklistMode;
  onUpdateCheckList: (newCheckList: TaskCheckList) => void;
  compact?: boolean;
  // Locks the checklist on top of what the completed state and the permissions already say, for a
  // caller that knows the task is frozen for a reason of its own (an audit execution task whose audit
  // is completed). Left out, those two decide on their own, as before.
  readonly?: boolean;
  // 'page' is the task detail view, passed on to the checklist; see Checklist.tsx
  variant?: 'default' | 'page';
  onPendingNewItemChange?: (hasPendingText: boolean) => void;
  // Hides the progress bar and the empty state while the checklist has no items, so an empty checklist
  // takes only the row of the new item editor (the new-task wizard).
  hideEmptyState?: boolean;
}

export const ChecklistContainer = (props: IChecklistContainerProps) => {
  const appContext = useContext(AppContext);
  //derived, not state: props.readonly can arrive after the first render (the audit status behind an
  //execution task is loaded asynchronously), and a state initializer would never see it
  const isReadOnly = props.readonly === true || props.orgTask.isCompleted();

  //
  // Main render
  //
  if (props.mode === ChecklistMode.Creator) {
    const allowEdit =
      !isReadOnly && canUpdateTaskField(props.orgTask, props.canUpdate, TaskFieldTypes.ChecklistEdit, appContext);

    return (
      <CheckList
        allowFill={false}
        allowEdit={allowEdit}
        allowComment={allowEdit}
        checkList={props.task.checkList}
        updateCheckList={props.onUpdateCheckList}
        compact={props.compact}
        variant={props.variant}
        onPendingNewItemChange={props.onPendingNewItemChange}
        hideEmptyState={props.hideEmptyState}
      />
    );
  } else {
    const allowFill =
      !isReadOnly && canUpdateTaskField(props.orgTask, props.canUpdate, TaskFieldTypes.ChecklistFill, appContext);

    const showProgress = !(props.hideEmptyState && props.task.checkList.items.length === 0);

    return (
      <IPStack verticalFill tokens={globalStackTokensGapSmall}>
        {showProgress && (
          <IPStack.Item styles={globalStackItemStylesPaddingScene}>
            <ChecklistProgress
              totalChecks={props.task.checkList.items ? props.task.checkList.items.length : 0}
              successChecks={props.task.checkList.getSuccessfulCount()}
              failedChecks={props.task.checkList.getFailedCount()}
            />
          </IPStack.Item>
        )}
        <IPStack.Item grow>
          <CheckList
            allowFill={allowFill}
            allowComment={allowFill}
            allowEdit={
              !isReadOnly &&
              canUpdateTaskField(props.orgTask, props.canUpdate, TaskFieldTypes.ChecklistEdit, appContext)
            }
            checkList={props.task.checkList}
            updateCheckList={props.onUpdateCheckList}
            compact={props.compact}
            variant={props.variant}
            onPendingNewItemChange={props.onPendingNewItemChange}
            hideEmptyState={props.hideEmptyState}
          />
        </IPStack.Item>
      </IPStack>
    );
  }
};
